import React, { useMemo, useRef, useState } from 'react';
import Utils, {
    HOURS_7_TO_22,
    matchesOperatorQuery,
    operatorName,
    operatorCode,
    shiftKindKey,
    shiftTimes,
    minutesToTime,
    barPosition,
    projectOperatorIds,
    SHIFT_KINDS,
    isWorkShift,
    snapMinutes,
    clampShiftTimes,
    moveShiftByDelta,
    minutesDeltaFromTrack,
} from '../utils';

const kindByKey = SHIFT_KINDS.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
}, {});

const KIND_SHORT = {
    m: 'утр',
    d: 'дн',
    e: 'веч',
    t: '2/2',
    s: 'бол',
    c: 'отм',
    p: 'пр',
};

const HoursHeader = () => (
    <div className="pc-day-gr pc-day-hours">
        <div className="pc-day-hname">Оператор</div>
        {HOURS_7_TO_22.map((hour) => (
            <div className="pc-day-hcell" key={hour}>
                <span className="pc-day-hfull">{String(hour).padStart(2, '0')}:00</span>
                <span className="pc-day-hshort">{hour}</span>
            </div>
        ))}
    </div>
);

const ShiftBar = ({ row, date, offKind, onCellClick, onShiftTimes }) => {
    const barRef = useRef(null);
    const dragRef = useRef(null);
    const [live, setLive] = useState(null);
    const canDrag = isWorkShift(row.shift) && typeof onShiftTimes === 'function';
    const times = live || row.times;
    const pos = barPosition(times.start, times.end);
    const duration = times.end - times.start;
    const fullLabel = `${minutesToTime(times.start)} – ${minutesToTime(times.end)}`;
    const label = duration <= 45 ? '' : duration <= 90 ? minutesToTime(times.start) : fullLabel;
    const sizeClass = duration <= 45 ? ' is-xs' : duration <= 90 ? ' is-sm' : '';

    const begin = (event, mode) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }
        if (!canDrag) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const track = event.currentTarget.closest('.pc-day-track');
        const width = track ? track.getBoundingClientRect().width : 1;
        if (barRef.current && event.pointerId != null) {
            barRef.current.setPointerCapture(event.pointerId);
        }
        dragRef.current = {
            mode,
            pointerId: event.pointerId,
            originX: event.clientX,
            start: row.times.start,
            end: row.times.end,
            width,
            moved: false,
            next: { start: row.times.start, end: row.times.end },
        };
    };

    const move = (event) => {
        const drag = dragRef.current;
        if (!drag || event.pointerId !== drag.pointerId) {
            return;
        }
        const deltaPx = event.clientX - drag.originX;
        if (Math.abs(deltaPx) > 4) {
            drag.moved = true;
        }
        const deltaMin = minutesDeltaFromTrack(deltaPx, drag.width);
        let next;
        if (drag.mode === 'start') {
            next = clampShiftTimes(drag.start + deltaMin, drag.end);
        } else if (drag.mode === 'end') {
            next = clampShiftTimes(drag.start, drag.end + deltaMin);
        } else {
            next = moveShiftByDelta(drag.start, drag.end, snapMinutes(deltaMin));
        }
        drag.next = next;
        setLive(next);
    };

    const finish = (event) => {
        const drag = dragRef.current;
        if (!drag || event.pointerId !== drag.pointerId) {
            return;
        }
        dragRef.current = null;
        try {
            if (barRef.current) {
                barRef.current.releasePointerCapture(event.pointerId);
            }
        } catch (error) {
            /* already released */
        }
        setLive(null);
        if (!drag.moved) {
            if (onCellClick) {
                onCellClick(row.operator, date, row.shift);
            }
            return;
        }
        const next = drag.next;
        if (!next || (next.start === drag.start && next.end === drag.end)) {
            return;
        }
        onShiftTimes(row.operator, date, row.shift, next.start, next.end);
    };

    return (
        <div
            ref={barRef}
            role="button"
            tabIndex={0}
            className={`pc-day-bar pc-day-bar-${row.key}${offKind ? ' dim' : ''}${live ? ' is-drag' : ''}${canDrag ? ' is-move' : ''}${sizeClass}`}
            style={pos}
            title={`${operatorName(row.operator)} · ${fullLabel}${canDrag ? ' · перетащите, шаг 30 мин' : ''}`}
            onPointerDown={(event) => {
                if (!canDrag) {
                    return;
                }
                begin(event, 'move');
            }}
            onPointerMove={move}
            onPointerUp={finish}
            onPointerCancel={finish}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (!canDrag && onCellClick) {
                    onCellClick(row.operator, date, row.shift);
                }
            }}
        >
            {canDrag ? (
                <span
                    className="pc-day-bar-h pc-day-bar-h-l"
                    onPointerDown={(event) => {
                        event.stopPropagation();
                        begin(event, 'start');
                    }}
                />
            ) : null}
            {label ? <span className="t">{label}</span> : null}
            {canDrag ? (
                <span
                    className="pc-day-bar-h pc-day-bar-h-r"
                    onPointerDown={(event) => {
                        event.stopPropagation();
                        begin(event, 'end');
                    }}
                />
            ) : null}
        </div>
    );
};

const DayView = ({
    operators = [],
    schedule = [],
    date,
    onCellClick,
    onShiftTimes,
    searchQuery = '',
    highlightedProjectId,
    projects = [],
    kindFilter = [],
    onOperatorClick,
}) => {
    const selectedProject = useMemo(
        () => (highlightedProjectId ? projects.find((item) => item._id === highlightedProjectId) : null),
        [highlightedProjectId, projects]
    );
    const selectedIds = useMemo(
        () => (selectedProject ? new Set(projectOperatorIds(selectedProject)) : null),
        [selectedProject]
    );

    const rows = useMemo(() => {
        return (operators || [])
            .filter((op) => matchesOperatorQuery(op, searchQuery))
            .map((operator) => {
                const id = operator.id || operator._id;
                const shift = Utils.getShiftForDate(schedule, id, date);
                const key = shiftKindKey(shift);
                const times = shift ? shiftTimes(shift) : null;
                return { operator, id, shift, key, times };
            })
            .filter((row) => row.shift)
            .sort((a, b) => (a.times.start || 0) - (b.times.start || 0));
    }, [operators, schedule, date, searchQuery]);

    return (
        <div className="pc-day-panel pc-day-panel--ops">
            <div className="pc-day-stick">
                <div className="pc-day-ptitle">
                    <h2>Операторы в смене</h2>
                    <span className="pc-sched-chip">{rows.length} в смене</span>
                    <div className="pc-day-rt">
                        <span className="pc-day-hint">перетащите смену · шаг 30 мин</span>
                    </div>
                </div>
                <HoursHeader />
            </div>
            {rows.map((row) => {
                const offProject = selectedIds && !selectedIds.has(String(row.id));
                const offKind = kindFilter.length > 0 && row.key && !kindFilter.includes(row.key);
                const kind = kindByKey[row.key] || kindByKey.d;
                const code = operatorCode(row.operator);
                const kindShort = KIND_SHORT[row.key] || (kind.n || '').toLowerCase();
                const name = operatorName(row.operator);
                return (
                    <div key={row.id} className={`pc-day-gr pc-day-oprow${offProject ? ' pc-day-opdim' : ''}`}>
                        <div
                            className="pc-day-opname"
                            title={`${name}${code ? ` · ${code}` : ''} · ${kind.n}`}
                        >
                            <button
                                type="button"
                                className="nm"
                                title={name}
                                onClick={() => onOperatorClick && onOperatorClick(row.id)}
                            >
                                {name}
                            </button>
                            <span className="cd">
                                {code ? <span className="cd-id">{code}</span> : null}
                                <span className={`cd-k cd-k-${row.key}`}>{kindShort}</span>
                            </span>
                        </div>
                        {HOURS_7_TO_22.map((hour) => (
                            <div className="pc-day-slot" key={hour} />
                        ))}
                        <div className="pc-day-track">
                            <ShiftBar
                                row={row}
                                date={date}
                                offKind={offKind}
                                onCellClick={onCellClick}
                                onShiftTimes={onShiftTimes}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default DayView;
